<script lang="ts" setup>

</script>

<template>
  <GenericDialog width="100vw">
    <template #trigger>
      <slot />
    </template>


    <template #body>

      <div class="py-12 container">
        <h1 class="scroll-m-20 text-4xl font-extrabold tracking-tight lg:text-5xl">Privacy Policy</h1>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Last updated: January 31, 2024</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">This Privacy Policy outlines our commitment to safeguarding your
          privacy and explains how we collect, use, and disclose information when you use our service. It also provides
          information about your privacy rights and how the law protects you.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We use your personal data solely to enhance and maintain our
          service. By using the service, you agree to the limited collection and use of information as outlined in this
          Privacy Policy.</p>
        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Interpretation and Definitions</h2>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Interpretation</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">The capitalized terms used in this policy have specific meanings
          as outlined below. The definitions apply whether the terms are used in singular or plural form.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Definitions</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">For the purposes of this Privacy Policy:</p>
        <ul>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Account</strong> means a unique account created for
              you to access our service.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Affiliate</strong> means an entity with a
              substantial ownership interest or common control.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Application</strong> refers to superseeded.ai, the
              software program provided by the Company.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Company</strong> refers to superseeded.ai.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Country</strong> refers to Victoria, Australia</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Device</strong> means any device that can access the
              service, such as a computer, a cellphone, or a digital tablet.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Personal Data</strong> is any information that
              relates to an identified or identifiable individual.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Service</strong> refers to the Application.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Service Provider</strong> means any natural or legal
              person who processes data on behalf of the Company.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>Usage Data</strong> refers to data collected
              automatically, generated by the use of the service or from the service infrastructure itself.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>You</strong> means the individual accessing or using
              the service.</p>
          </li>
        </ul>
        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Collecting and Using Your Personal Data</h2>

        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">While using our service, we may ask you to provide us with
          certain personally identifiable information for the sole purpose of improving the user experience. Personally
          identifiable information may include, but is not limited to:</p>
        <ul class="my-6 ml-6 list-disc [&>li]:mt-2">
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6">Email address</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6">First name and last name</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6">Usage Data</p>
          </li>
        </ul>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Usage Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Usage Data is collected automatically for the purpose of
          analyzing service usage and improving its functionality.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Usage Data may include information such as your device's
          Internet Protocol address, browser type, pages visited, time and date of visit, and other diagnostic data.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">When accessing the service through a mobile device, we may
          collect additional information, including device type, IP address, mobile operating system, and browser type.
        </p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We may also collect information sent by your browser when you
          visit our service or use it on a mobile device.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Use of Your Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We may use Personal Data for the following limited purposes:</p>
        <ul>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>To provide and maintain our service</strong>,
              including monitoring service usage.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>To manage your Account:</strong> for user
              registration and access to service functionalities.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>For the performance of a contract:</strong> to
              fulfill the purchase contract or other agreements with you.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>To contact you:</strong> for essential
              communications related to service updates or informative messages.</p>
          </li>
          <li>
            <p class="leading-7 [&:not(:first-child)]:mt-6"><strong>For other purposes</strong>: for limited purposes
              like data analysis, identifying usage trends, and improving our service.</p>
          </li>
        </ul>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We may share your information in specific situations:</p>
        <ul>
          <li><strong>With Your consent</strong>: Information may be disclosed for specific purposes with your consent.
          </li>
        </ul>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Retention of Your Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We will retain your Personal Data only for as long as necessary
          to fulfill the purposes outlined in this Privacy Policy or as required by law. Usage Data is retained for
          internal analysis purposes or as needed to enhance service security and functionality.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Transfer of Your Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Your information, including Personal Data, may be processed and
          maintained outside your jurisdiction. By using the service and submitting information, you consent to such
          transfer.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We will take reasonable steps to ensure that your data is
          treated securely and in line with this Privacy Policy, maintaining adequate controls for data security.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Delete Your Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">You have the right to request deletion of your Personal Data.
          You may update, amend, or delete your information through your Account settings or by contacting us. However,
          certain information may be retained due to legal obligations.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Disclosure of Your Personal Data</h3>
        <h4>Business Transactions</h4>
        <p class="leading-7 [&:not(:first-child)]:mt-6">In the event of a merger, acquisition, or asset sale, your
          Personal Data may be transferred. Notice will be provided before such transfer, and it will be subject to a
          similar Privacy Policy.</p>
        <h4>Law enforcement</h4>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Under certain circumstances, we may disclose your Personal Data
          if required by law or in response to valid requests by public authorities.</p>
        <h4>Other legal requirements</h4>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We may disclose your Personal Data in good faith belief that
          such action is necessary to comply with legal obligations, protect and defend rights or property, prevent
          wrongdoing, or protect personal safety.</p>
        <h3 class="mt-8 scroll-m-20 text-2xl font-semibold tracking-tight">Security of Your Personal Data</h3>
        <p class="leading-7 [&:not(:first-child)]:mt-6">While we strive to use commercially acceptable means to protect
          your Personal Data, we cannot guarantee absolute security. The security of your data is important to us, and
          we implement reasonable measures to protect it.</p>
        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Children's Privacy</h2>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Our service is not directed at individuals under the age of 13.
          We do not knowingly collect personally identifiable information from anyone under 13. If you are a parent or
          guardian and aware that your child has provided us with Personal Data, please contact us. We take steps to
          remove such information if collected without verification of parental consent.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">If consent is required from a parent in your country, we may
          request it before collecting and using information.</p>


        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Google API Privacy Policy </h2>
        <p class="leading-7 [&:not(:first-child)]:mt-6">This Privacy Policy works in conjunction with the Google Privacy
          Policy, accessible at <NuxtLink target="_blank" to="https://policies.google.com/privacy"
            class="underline text-blue-600">https://policies.google.com/privacy</NuxtLink>. Please review the Google
          Privacy Policy to understand how Google collects, uses, and protects your information, as it may apply to your
          interactions with superseeded.ai</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">By using superseeded.ai, you also agree to comply with the
          Google Privacy Policy.
        </p>

        <p class="leading-7 [&:not(:first-child)]:mt-6">By using superseeded.ai you acknowledge and agree that the API
          Client, or third parties authorized by the API Client, may store, access, or collect information directly or
          indirectly on or from your devices. This includes the use of technologies such as cookies or similar
          technologies on your devices or browsers.</p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">
          The information collected may include but is not limited to:
        </p>
        <ul class="my-6 ml-6 list-disc [&>li]:mt-2">
          <li>
            Device information, such as device type, operating system, and unique device identifiers
          </li>
          <li>
            Usage data, including interactions with the API Client, preferences, and settings.
          </li>
          <li>
            Location data, if you grant permission for location-based features
          </li>
        </ul>
        <p class="leading-7 [&:not(:first-child)]:mt-6">
          This information is utilized for the purpose of providing and improving the functionality of superseeded.ai,
          as well as for analytics and personalized content delivery.
        </p>
        <p class="leading-7 [&:not(:first-child)]:mt-6">
          You have the option to manage cookie preferences and other tracking technologies through your device or
          browser settings. Please refer to your device or browser documentation for more information on how to control
          these settings.
        </p>


        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Links to Other Websites</h2>
        <p class="leading-7 [&:not(:first-child)]:mt-6">Our service may contain links to other websites not operated by
          us. We advise you to review the Privacy Policy of every site you visit. We have no control over third-party
          sites and assume no responsibility for their content, privacy policies, or practices.</p>
        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Changes to this Privacy Policy</h2>
        <p class="leading-7 [&:not(:first-child)]:mt-6">We may update our Privacy Policy from time to time. Changes will
          be posted on this page, and we will notify you via email or a prominent notice on our service before changes
          take effect. Review this Privacy Policy periodically for any updates, as changes become effective upon
          posting.</p>
        <h2 class="mt-10 scroll-m-20 border-b pb-2 text-3xl font-semibold tracking-tight transition-colors first:mt-0">
          Contact Us</h2>
        <p class="leading-7 [&:not(:first-child)]:mt-6">If you have questions about this Privacy Policy, you can contact
          us via email at <a class="underline text-blue-600"
            href="mailto:hello@superseeded.ai">hello@superseeded.ai</a>.</p>
      </div>

    </template>

  </GenericDialog>
</template>

<style scoped></style>
